import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, RefreshCw } from "lucide-react";
import { API } from "@/lib/api";

const VERB_COLORS = { POST: "#059669", PUT: "#D97706", PATCH: "#D97706", DELETE: "#DC2626" };

export const AuditLogCard = ({ hasFeature }) => {
  const enabled = hasFeature("multi_user");
  const [data, setData] = useState({ rows: [], areas: [] });
  const [area, setArea] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (areaF = "", qF = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit-log`, { params: { area: areaF, q: qF, limit: 100 } });
      setData(r.data || { rows: [], areas: [] });
    } catch { setData({ rows: [], areas: [] }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  return (
    <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-audit-card">
      <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1 flex items-center gap-2">
        <ScrollText className="w-5 h-5 text-[var(--brand-primary)]" /> Audit-Log
      </h2>
      <p className="text-sm text-[var(--brand-muted)] mb-4">
        Lückenlose Änderungshistorie: Wer hat wann was erstellt, geändert oder gelöscht – wichtig für Compliance &amp; größere Kunden.
      </p>

      {!enabled ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="audit-upsell">
          Das Audit-Log ist im <b>Business-Tarif</b> enthalten.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <select value={area} onChange={(e) => { setArea(e.target.value); load(e.target.value, q); }}
              className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm" data-testid="audit-area-filter">
              <option value="">Alle Bereiche</option>
              {data.areas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(area, q); }}
              placeholder="Suche (Nutzer oder Pfad) …" className="h-9 w-56" data-testid="audit-search" />
            <Button variant="outline" size="sm" onClick={() => load(area, q)} className="border-[var(--brand-border)] h-9" data-testid="audit-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-[var(--brand-muted)]" data-testid="audit-empty">Noch keine Einträge – Änderungen werden ab jetzt automatisch protokolliert.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto rounded-lg border border-[var(--brand-border-light)]" data-testid="audit-rows">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[var(--brand-bg)]">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[var(--brand-muted)]">
                    <th className="py-2 px-3">Zeitpunkt</th>
                    <th className="py-2 px-3">Nutzer</th>
                    <th className="py-2 px-3">Aktion</th>
                    <th className="py-2 px-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--brand-border-light)]" data-testid={`audit-row-${r.id}`}>
                      <td className="py-1.5 px-3 whitespace-nowrap text-[var(--brand-muted)]">
                        {new Date(r.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="py-1.5 px-3">{r.actor}{r.role === "mitarbeiter" && <span className="text-[10px] text-[var(--brand-muted)]"> (MA)</span>}</td>
                      <td className="py-1.5 px-3">
                        <span className="font-medium" style={{ color: VERB_COLORS[r.method] || "inherit" }}>{r.action}</span>
                      </td>
                      <td className="py-1.5 px-3 text-[11px] text-[var(--brand-muted)] font-mono truncate max-w-[220px]" title={r.path}>{r.method} {r.path.replace("/api/", "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
};
